import React from "react";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, Monitor, Smartphone, Globe } from "lucide-react";
import SectionHeading from "./SectionHeading";
import { fadeVariants, staggerVariants, VIEWPORT_ONCE, VIEWPORT_ONCE_WIDE } from "../lib/motion";

const PLATFORM_IMG =
    "https://static.prod-images.emergentagent.com/jobs/51f38290-0410-404e-b160-085423ca1b2a/images/ce24a215838e676ed65d7b39ebe604e1af8611be7fb37b371eca19451cafa5bf.png";

const features = [
    "21 timeframes from M1 to MN",
    "38 built-in indicators + custom MQL5",
    "Algorithmic trading via Expert Advisors",
    "Depth-of-market & one-click trading",
    "Multi-currency strategy tester",
    "Economic calendar inside the platform",
];

const imageEnter = fadeVariants({ x: -30, duration: 0.7 });
const listItemVariants = staggerVariants({ x: -10, duration: 0.4, step: 0.05 });

const floatLoop = {
    animate: { y: [0, -10, 0] },
    transition: { duration: 6, repeat: Infinity },
};

function PreviewImage() {
    return (
        <motion.div
            variants={imageEnter}
            initial="hidden"
            whileInView="visible"
            viewport={VIEWPORT_ONCE_WIDE}
            className="relative order-2 lg:order-1"
        >
            <div className="absolute -inset-4 bg-[#A6E3DC]/10 rounded-3xl blur-2xl" />
            <img
                src={PLATFORM_IMG}
                alt="MT5 Platform"
                className="relative rounded-2xl border border-white/10 w-full"
            />
            <motion.div
                {...floatLoop}
                className="absolute -top-5 -right-5 xpo-glass p-4 hidden sm:block"
            >
                <p className="text-[10px] uppercase tracking-widest text-slate-400">
                    Today's Volume
                </p>
                <p className="font-mono text-2xl font-bold text-white mt-1">
                    $4.21B
                </p>
                <p className="text-[#00FFC2] text-xs mt-0.5">▲ 12.4%</p>
            </motion.div>
        </motion.div>
    );
}

function FeatureChecklist() {
    return (
        <ul className="mt-8 space-y-3">
            {features.map((f, i) => (
                <motion.li
                    key={f}
                    variants={listItemVariants}
                    initial="hidden"
                    whileInView="visible"
                    custom={i}
                    viewport={VIEWPORT_ONCE}
                    className="flex items-start gap-3 text-slate-300"
                >
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-[#A6E3DC]/15 grid place-items-center flex-shrink-0">
                        <Check size={12} className="text-[#A6E3DC]" />
                    </span>
                    <span>{f}</span>
                </motion.li>
            ))}
        </ul>
    );
}

function PlatformCTARow() {
    return (
        <div className="flex flex-wrap items-center gap-3 mt-10">
            <a
                data-testid="platform-preview-cta"
                href="/platform"
                className="btn-outline-mint"
            >
                See platform details <ArrowUpRight size={16} />
            </a>
            <div className="flex items-center gap-4 text-xs text-slate-400 ml-2">
                <span className="flex items-center gap-1.5">
                    <Monitor size={14} /> Desktop
                </span>
                <span className="flex items-center gap-1.5">
                    <Globe size={14} /> Web
                </span>
                <span className="flex items-center gap-1.5">
                    <Smartphone size={14} /> Mobile
                </span>
            </div>
        </div>
    );
}

export default function PlatformPreview() {
    return (
        <section
            data-testid="platform-preview-section"
            className="relative py-24 lg:py-32 bg-[#0A0E23]/40"
        >
            <div className="absolute top-1/2 -left-32 w-96 h-96 xpo-glow-mint blur-3xl -z-10" />
            <div className="xpo-container">
                <div className="grid lg:grid-cols-2 gap-14 items-center">
                    <PreviewImage />
                    <div className="order-1 lg:order-2">
                        <SectionHeading
                            overline="Platform"
                            title="MetaTrader 5 — every workflow, one engine."
                            description="Whether you scalp the New York open or run multi-currency portfolios, MT5 gives you institutional-grade tools with retail simplicity."
                        />
                        <FeatureChecklist />
                        <PlatformCTARow />
                    </div>
                </div>
            </div>
        </section>
    );
}
